import { useState, useEffect, useCallback } from 'react';
import { GridValidRowModel } from '@mui/x-data-grid';
import { IconButton, Box, Typography } from '@mui/material';
import moment from 'moment-timezone';
import DialogModal from '../Modal/DialogModal';
import '../DataPipeline/dataPipeline.scss';
import '../DataEstate/data-estate.scss';
import { getKpiFrameworkConfigDetails, getKpiFrameworkIngestData } from '../../service/service';
import Loader from '../Loader/loader';
import { useParams } from 'react-router-dom';
import { applyValue, deleteValue, JsonViewer, JsonViewerOnAdd, JsonViewerOnChange, JsonViewerOnDelete } from '@textea/json-viewer';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { exportToXlsx } from 'components/exportUtils';
import PageWrapper from 'components/Common/PageWraper';

interface KPIConfig extends GridValidRowModel {
	_id: string;
	name: string;
	description: string;
	last_run_time: string;
	next_run_time: string;
	frequency: string;
	isActive: boolean;
}

interface KPIResult extends GridValidRowModel {
	id: string;
	metricName: string;
	metricValue: string;
}

const getDateRangeFromFilter = (selectedDate: string) => {
	switch (selectedDate) {
		case '0M':
			return { startDate: moment().startOf('month'), endDate: moment().endOf('day') };
		case '1M':
			return { startDate: moment().subtract(1, 'months').startOf('month'), endDate: moment().subtract(1, 'months').endOf('month') };
		case '3M':
			return { startDate: moment().subtract(3, 'months').startOf('day'), endDate: moment().endOf('day') };
		case '6M':
			return { startDate: moment().subtract(6, 'months').startOf('day'), endDate: moment().endOf('day') };
		case '9M':
			return { startDate: moment().subtract(9, 'months').startOf('day'), endDate: moment().endOf('day') };
		case '0Y':
			return { startDate: moment().startOf('year'), endDate: moment().endOf('day') };
		case '1Y':
			return { startDate: moment().subtract(1, 'years').startOf('year'), endDate: moment().subtract(1, 'years').endOf('year') };
		case '2Y':
			return { startDate: moment().subtract(2, 'years').startOf('day'), endDate: moment().endOf('day') };
		default:
			if (/^\d{4}-\d{2}$/.test(selectedDate)) {
				return {
					startDate: moment(selectedDate, 'YYYY-MM').startOf('month'),
					endDate: moment(selectedDate, 'YYYY-MM').endOf('month'),
				};
			} else if (/^\d{4}$/.test(selectedDate)) {
				return {
					startDate: moment(selectedDate, 'YYYY').startOf('year'),
					endDate: moment(selectedDate, 'YYYY').endOf('year'),
				};
			}
			return { startDate: moment().startOf('month'), endDate: moment().endOf('day') };
	}
};

const KPIDetails = () => {
	const [configDetails, setConfigDetails] = useState<any>({})
	const [kpiResults, setKpiResults] = useState<any[]>([])
	const [loading, setLoading] = useState(false)
	const { id: configId } = useParams<{ id: string }>()
	const [showSourceDialog, setShowSourceDialog] = useState(false)
	const [showTargetDialog, setShowTargetDialog] = useState(false)
	const [sourceConfig, setSourceConfig] = useState<any>({})
	const [totalCount, setTotalCount] = useState(0)
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const initialSelectedDate = sessionStorage.getItem('dataEstateFilterMonth') || '0M';
	const initialRange = getDateRangeFromFilter(initialSelectedDate);
	const [fromDateTime, setFromDateTime] = useState<any>(initialRange.startDate);
	const [toDateTime, setToDateTime] = useState<any>(initialRange.endDate);
	const defaultBody = { page: 1, pageSize: 10, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: initialRange.startDate, endDate: initialRange.endDate } };

	let defaultColumns: any = [];

	const handleSorting = (sortingInfo: any) => {
		if (sortingInfo.sortBy === 'createdDt' || sortingInfo.sortBy === '') {
			setSortingInfo({ sortBy: sortingInfo.sortBy, sortDir: sortingInfo.sortDir });
			fetchKpiResult({ ...defaultBody, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: fromDateTime, endDate: toDateTime } });
		}
	};

	const handlePageChange = (data: any, page: any) => {
		setPage(page)
		fetchKpiResult({ page, pageSize, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: fromDateTime, endDate: toDateTime } })
	}

	const handlePageSizeChange = (event: any) => {
		setPageSize(parseInt(event.target.value))
		fetchKpiResult({ page, pageSize: event.target.value, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: fromDateTime, endDate: toDateTime } })
	}

	const handleTimeFilter = () => {
		setPage(1)
		fetchKpiResult({ page: 1, pageSize, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: fromDateTime, endDate: toDateTime } })
	}

	const clearTimeFilter = () => {
		const defaultRange = getDateRangeFromFilter('0M')
		setFromDateTime(defaultRange.startDate)
		setToDateTime(defaultRange.endDate)
		setPage(1)
		fetchKpiResult({ page: 1, pageSize, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir, filter: { startDate: defaultRange.startDate, endDate: defaultRange.endDate } })
	}

	function getColumns() {
		const columnNames = kpiResults?.length ? Object.keys(kpiResults[0]) : []
		const columns: any = kpiResults?.length ? columnNames.map(metricName => {
			const name = metricName === 'createdDt' ? 'Created At' : metricName === 'ingestId' ? 'Ingest ID' : metricName
			defaultColumns.push(name)
			return {
				key: metricName,
				name,
				minWidth: 100,
				width: 330,
				sortable: false,
				formatter({ row }: any) {
					const data = row[metricName]
					return (Array.isArray(data) ? data.length : Object.keys(data).length ? JSON.stringify(data) : data)
				}
			}
		}) : []
		return columns
	}
	const fetchKpiResult = async (body: any) => {
		if (body.sortColumn === 'createdDt') {
			body.sortColumn = 'createdAt'
		}
		setLoading(true)
		const response = configId ? await getKpiFrameworkIngestData(body, configId?.toString()) : { data: [] }
		setLoading(false)
		if (response.data?.data?.length) {
			const data = response.data.data.reduce((finalData: any, item: any) => {
				finalData.push({ ingestId: item._id, ...item.kpiResult, createdDt: moment(item.createdAt).format() })
				return finalData
			}, [])
			setKpiResults(data)
			setTotalCount(response.data.totalCount)
		}
	}

	useEffect(() => {
		const fetchKpiConfigDetails = async () => {
			setLoading(true);
			try {
				const result = configId ? await getKpiFrameworkConfigDetails(configId?.toString()) : { data: [] };
				setConfigDetails(result.data?.kpiConfig || {});
				if (result.data?.kpiConfig?.source_config) {
					setSourceConfig(result.data.kpiConfig.source_config)
				}
			} catch (error) {
				console.error('Error fetching dashboard data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchKpiConfigDetails();
		fetchKpiResult(defaultBody)
	}, []);

	useEffect(() => {
		const onDateChange = (event: any) => {
			const selectedDate = event?.detail?.selectedDate;
			if (selectedDate) {
				const range = getDateRangeFromFilter(selectedDate);
				setFromDateTime(range.startDate);
				setToDateTime(range.endDate);
				setPage(1);
				fetchKpiResult({
					page: 1,
					pageSize,
					sortColumn: sortingInfo.sortBy,
					sortDir: sortingInfo.sortDir,
					filter: { startDate: range.startDate, endDate: range.endDate }
				});
			}
		};
		window.addEventListener('dataEstateDateChanged', onDateChange);
		return () => {
			window.removeEventListener('dataEstateDateChanged', onDateChange);
		};
	}, [pageSize, sortingInfo, configId]);

	const formatDate = (date: any) => {
		if (!date) return '';
		const m = moment(date);
		return m.isValid() ? m.format('DD-MM-YYYY HH:mm:ss') : String(date);
	};

	const InfoField = ({ label, value, isLink, onClick, minWidth, flex }: {
		label: string;
		value?: React.ReactNode;
		isLink?: boolean;
		onClick?: () => void;
		minWidth?: string | number;
		flex?: string | number;
	}) => (
		<Box sx={{ minWidth, flex, flexShrink: 0 }}>
			<Typography sx={{ fontSize: '12px', color: '#71758F', mb: 0.5, fontWeight: 400 }}>
				{label}
			</Typography>
			{isLink ? (
				<Typography
					onClick={onClick}
					sx={{
						fontSize: '14px',
						color: '#009BD8',
						fontWeight: 500,
						textDecoration: 'underline',
						cursor: 'pointer',
						whiteSpace: 'nowrap',
					}}
				>
					{value || ''}
				</Typography>
			) : (
				<Typography sx={{ fontSize: '14px', color: '#1B1E35', fontWeight: 500, whiteSpace: flex ? 'normal' : 'nowrap' }}>
					{value || ''}
				</Typography>
			)}
		</Box>
	);

	return (
		<PageWrapper sx={{ mt: { xs: '70px', sm: '50px', md: '60px', lg: '46px' } }}>
			<PageHeader
				title={[{ text: 'KPI Store', link: '/kpiFramework/configs' }, { text: `${configDetails?.name || ''}` }]}
				isPepwise2Breadcrumb
				hasEllipsis={false}
				headerSx={{ top: '78px', mb: 2, paddingTop: '16px' }}
			/>
			<DialogModal title="Source Config" isOpen={showSourceDialog} handleClose={() => setShowSourceDialog(false)} titleSx={{padding: '8px 20px'}} dialogContentProps={{
				sx: {padding: '8px 12px'}
			}}>
				<JsonViewer
					editable
					style={{ textAlign: 'left' }}
					theme={'dark'}
					value={sourceConfig}
					onAdd={useCallback<JsonViewerOnAdd>((path) => {
						const key = prompt('Key:');
						if (key === null) return;
						const value = prompt('Value:');
						if (value === null) return;
						setSourceConfig((src: any) => applyValue(src, [...path, key], value));
					}, [])}
					onChange={useCallback<JsonViewerOnChange>((path, oldValue, newValue) => {
						setSourceConfig((src: any) => applyValue(src, path, newValue));
					}, [])}
					onDelete={useCallback<JsonViewerOnDelete>((path, value) => {
						setSourceConfig((src: any) => deleteValue(src, path, value));
					}, [])}
				/>
			</DialogModal>
			<DialogModal title="Target Config" isOpen={showTargetDialog} handleClose={() => setShowTargetDialog(false)} titleSx={{padding: '8px 20px'}} dialogContentProps={{
				sx: {padding: '8px 12px'}
			}}>
				<JsonViewer style={{ textAlign: 'left' }} theme={'dark'} value={configDetails?.target_config} />
			</DialogModal>

			<Box
				sx={{
					backgroundColor: '#FBFDFF',
					boxShadow: '4px 8px 54px 0px #00000014',
					padding: '16px 20px',
					paddingRight: '48px',
					borderRadius: '8px',
					position: 'relative',
					display: 'flex',
					flexWrap: 'wrap',
					alignItems: 'flex-start',
					rowGap: '16px',
					columnGap: { xs: '20px', md: '28px', lg: '34px' },
					my: 2,
				}}
			>
				<InfoField label="ID" value={configDetails?._id} />
				<InfoField label="Sector" value={configDetails?.sector} />
				<InfoField label="Program" value={configDetails?.program} />
				<InfoField label="Project" value={configDetails?.project} />
				<InfoField
					label="Frequency"
					value={configDetails?.update_frequency_value ? `${configDetails.update_frequency_value} ${configDetails.update_frequency_unit || ''}` : configDetails?.frequency}
				/>
				<InfoField
					label="Sources"
					value={configDetails?.source_config?.type || 'es'}
					isLink
					onClick={() => setShowSourceDialog(true)}
				/>
				<InfoField
					label="Target Type"
					value={configDetails?.target_config?.type || 'cosmosdb'}
					isLink
					onClick={() => setShowTargetDialog(true)}
				/>
				<InfoField label="Status" value={configDetails?.isActive ? 'Active' : 'Inactive'} />
				<InfoField label="Last Run Time" value={formatDate(configDetails?.last_run_time)} />
				<InfoField label="Next Run Time" value={formatDate(configDetails?.next_run_time)} />
				<InfoField label="Created By" value={configDetails?.createdByUser?.[0] || configDetails?.createdByUser || ''} />
				<InfoField label="Updated By" value={configDetails?.updatedByUser?.[0] || configDetails?.updatedByUser || ''} />
				<InfoField label="Created At" value={formatDate(configDetails?.createdAt)} minWidth="150px" />
				<InfoField label="Updated At" value={formatDate(configDetails?.updatedAt)} minWidth="150px" />
				<InfoField label="Description" value={configDetails?.description} minWidth="200px" flex={1} />

			</Box>
			<Box sx={{
				'--rdg-border-color': '#EFF6FF',
				'& .rdg .rdg-header-row .rdg-cell, & .rdg-header-row .rdg-cell': {
					borderRight: '1px solid #EFF6FF !important',
					borderInlineEnd: '1px solid #EFF6FF !important',
					'&:last-child': {
						borderRight: 'none !important',
						borderInlineEnd: 'none !important',
					},
				},
				'& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(even), & .rdg-row:not(.rdg-header-row):nth-of-type(even), & .rdg .rdg-row.rdg-row-even, & .rdg-row.rdg-row-even': {
					backgroundColor: '#EFF6FF !important',
				},
				'& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(even) .rdg-cell, & .rdg-row:not(.rdg-header-row):nth-of-type(even) .rdg-cell, & .rdg .rdg-row.rdg-row-even .rdg-cell, & .rdg-row.rdg-row-even .rdg-cell': {
					backgroundColor: '#EFF6FF !important',
				},
				'& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(odd), & .rdg-row:not(.rdg-header-row):nth-of-type(odd), & .rdg .rdg-row.rdg-row-odd, & .rdg-row.rdg-row-odd': {
					backgroundColor: '#71758F0D !important',
				},
				'& .rdg .rdg-row:not(.rdg-header-row):nth-of-type(odd) .rdg-cell, & .rdg-row:not(.rdg-header-row):nth-of-type(odd) .rdg-cell, & .rdg .rdg-row.rdg-row-odd .rdg-cell, & .rdg-row.rdg-row-odd .rdg-cell': {
					backgroundColor: '#71758F0D !important',
				},
				'& .rdg .rdg-row:not(.rdg-header-row) .rdg-cell, & .rdg-row:not(.rdg-header-row) .rdg-cell': {
					borderRight: '1px solid #EFF6FF !important',
					borderInlineEnd: '1px solid #EFF6FF !important',
					borderBottom: '1px solid #EFF6FF !important',
					borderBlockEnd: '1px solid #EFF6FF !important',
					borderColor: '#EFF6FF !important',
					'&:last-child': {
						borderRight: 'none !important',
						borderInlineEnd: 'none !important',
					},
				},
			}}>
				{loading ? (
					<Loader />
				) : (
					<CustomizedDataGrid
						title="KPI Output"
						tableColumns={getColumns()}
						tableData={kpiResults || []}
						tableType="KPIResults"
						defaultColumnNames={defaultColumns}
						listLength={totalCount || 0}
						page={page}
						pageSize={pageSize}
						paginationData={{ totalElements: totalCount, totalPages: Math.ceil(totalCount / pageSize) }}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						showExport={true}
						fileName={`KPIResults-${moment().format()}.xlsx`}
						exportTableData={() => exportToXlsx(`KPIResults-${moment().format()}.xlsx`, kpiResults, getColumns())}
						handlepageSizeChange={handlePageSizeChange}
						handlePageChange={handlePageChange}
						isDateRangeFilter={true}
						isDisabled={false}
						rowHeight={40}
					/>
				)}
			</Box>
		</PageWrapper>
	);
};

export default KPIDetails;
